import React, { useState } from 'react';
import { useTranslations } from '../translations';
import { recoverPassword } from '../services/auth';
import { ApiError } from '../services/api.error';
import { useNotification } from './NotificationProvider';
import './ForgotPasswordModal.css';

interface ForgotPasswordModalProps {
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ onClose }) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError(t.forgotPassword.emailRequired);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await recoverPassword({ email: email.trim() });

      showNotification(t.forgotPassword.successMessage, 'success');
      onClose();
    } catch (err) {
      setError(err instanceof ApiError && (err.status === 429 || err.hasErrorCode('authentication_rate_limited'))
        ? t.forgotPassword.rateLimited
        : t.forgotPassword.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="forgot-password-modal-overlay" onClick={handleOverlayClick}>
      <div className="forgot-password-modal-container">
        <div className="forgot-password-modal-bg"></div>

        <button className="forgot-password-modal-close" onClick={onClose} aria-label="Close">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M28.7 11.3C28.3 10.9 27.7 10.9 27.3 11.3L20 18.6L12.7 11.3C12.3 10.9 11.7 10.9 11.3 11.3C10.9 11.7 10.9 12.3 11.3 12.7L18.6 20L11.3 27.3C10.9 27.7 10.9 28.3 11.3 28.7C11.5 28.9 11.7 29 12 29C12.3 29 12.5 28.9 12.7 28.7L20 21.4L27.3 28.7C27.5 28.9 27.7 29 28 29C28.3 29 28.5 28.9 28.7 28.7C29.1 28.3 29.1 27.7 28.7 27.3L21.4 20L28.7 12.7C29.1 12.3 29.1 11.7 28.7 11.3Z" fill="#071105"/>
          </svg>
        </button>

        <h2 className="forgot-password-modal-title">{t.forgotPassword.title}</h2>
        <p className="forgot-password-modal-description">{t.forgotPassword.description}</p>

        <div className="forgot-password-modal-field">
          <label className="forgot-password-modal-label">
            {t.forgotPassword.emailLabel}
            <span className="required-asterisk">*</span>
          </label>
          <input
            type="email"
            className={`forgot-password-modal-input ${error ? 'forgot-password-modal-input-error' : ''}`}
            placeholder={t.forgotPassword.emailPlaceholder}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
          />
        </div>

        {error && (
          <div className="forgot-password-modal-error">
            {error}
          </div>
        )}

        <div className="forgot-password-modal-actions">
          <button type="button" className="forgot-password-modal-cancel" onClick={onClose} disabled={isSubmitting}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="forgot-password-modal-submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t.forgotPassword.submitting : t.forgotPassword.submitButton}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
